/**
 * PRD Detail Page
 *
 * View and edit a specific PRD with tabbed interface
 */

import * as React from 'react';
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { ArrowLeft, Download } from 'lucide-react';
import {
  Button,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@forgenetwork/ui';
import { PRDViewer, PRDEditor } from '@/components/prd';
import { usePRD, useExportPRD } from '@/hooks/usePRD';
import { useToast } from '@/shared/hooks/useToast';

// Search params type
type PRDSearchParams = {
  tab?: 'view' | 'edit';
};

export const Route = createFileRoute('/_authenticated/prd/$prdId')({
  component: PRDDetailPage,
  validateSearch: (search: Record<string, unknown>): PRDSearchParams => {
    return {
      tab: (search.tab as 'view' | 'edit') ?? 'view',
    };
  },
});

function PRDDetailPage(): React.JSX.Element {
  const { prdId } = Route.useParams();
  const search = useSearch({ from: '/_authenticated/prd/$prdId' });
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data } = usePRD(prdId);
  const exportPRD = useExportPRD();

  const [activeTab, setActiveTab] = React.useState<'view' | 'edit'>(search.tab ?? 'view');

  // Update URL when tab changes
  const handleTabChange = (value: string): void => {
    const tab = value as 'view' | 'edit';
    setActiveTab(tab);
    navigate({
      to: '/prd/$prdId',
      params: { prdId },
      search: { tab },
      replace: true,
    });
  };

  const handleExport = (format: 'markdown' | 'json' | 'html' = 'markdown'): void => {
    exportPRD.mutate(
      { prdId, format },
      {
        onSuccess: (result) => {
          toast({
            title: 'Export Successful',
            description: `PRD exported as ${result.filename}`,
          });
        },
        onError: (error: Error) => {
          toast({
            title: 'Export Failed',
            description: error.message || 'Failed to export PRD',
            variant: 'destructive',
          });
        },
      }
    );
  };

  const handleSave = (): void => {
    toast({
      title: 'PRD Saved',
      description: 'Your changes have been saved successfully.',
    });
    // Switch back to view mode after save
    handleTabChange('view');
  };

  const handleCancel = (): void => {
    handleTabChange('view');
  };

  const handleBack = (): void => {
    if (data?.prd) {
      navigate({
        to: '/repositories/$repoId/prd',
        params: { repoId: data.prd.repositoryId },
      });
    } else {
      navigate({ to: '/repositories' });
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBack}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold tracking-tight">
                {data?.prd.title ?? 'Product Requirements Document'}
              </h1>
              <p className="text-muted-foreground mt-1">
                View and edit PRD details
              </p>
            </div>
          </div>
          {activeTab === 'view' && (
            <Button
              variant="outline"
              onClick={() => handleExport('markdown')}
              disabled={exportPRD.isPending}
              className="gap-2"
            >
              <Download className="h-4 w-4" />
              {exportPRD.isPending ? 'Exporting...' : 'Export'}
            </Button>
          )}
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="view">View</TabsTrigger>
            <TabsTrigger value="edit">Edit</TabsTrigger>
          </TabsList>

          <TabsContent value="view" className="mt-6">
            <PRDViewer prdId={prdId} onExport={() => handleExport('markdown')} />
          </TabsContent>

          <TabsContent value="edit" className="mt-6">
            <PRDEditor
              prdId={prdId}
              onSave={handleSave}
              onCancel={handleCancel}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
